import React, { useEffect, useState } from "react";
import { Loader2, Sparkles, Mail, Phone, Wand2, ShieldCheck, Info, PhoneCall, ThumbsUp, ThumbsDown } from "lucide-react";
import { toast } from "sonner";
import { leadsApi } from "@/lib/leadsApi";
import { extrasApi, bl } from "@/lib/extrasApi";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { Avatar } from "@/components/find/atoms";
import { Checkbox } from "@/components/ui/checkbox";

export default function Enrich() {
    const { locale } = useI18n();
    const { refresh } = useAuth();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(new Set());
    const [busy, setBusy] = useState(null); // null | "reveal-email" | "reveal-phone" | "ai" | "hunter"
    const [providers, setProviders] = useState(null);
    const [ratings, setRatings] = useState({}); // lead_id -> "up"|"down"|""
    const [quota, setQuota] = useState(null);

    async function load() {
        setLoading(true);
        try {
            const r = await leadsApi.list({ page: 1, page_size: 100, sort: "-score" });
            setItems(r.items);
            setTotal(r.total);
            if (r.items.length) {
                extrasApi.ratings.bulkGet(r.items.map(l => l.lead_id))
                    .then(({ ratings }) => setRatings(ratings || {}))
                    .catch(() => {});
            }
        } catch (_e) { toast.error("Failed"); }
        finally { setLoading(false); }
    }
    useEffect(() => {
        load();
        extrasApi.enrich.providers().then(setProviders).catch(() => {});
        extrasApi.ratings.quota().then(setQuota).catch(() => {});
    }, []);

    async function rateLead(leadId, newRating) {
        try {
            const res = await extrasApi.ratings.rate(leadId, newRating);
            setRatings((r) => ({ ...r, [leadId]: newRating }));
            refresh();
            extrasApi.ratings.quota().then(setQuota).catch(() => {});
            if (res.credits_refunded > 0) {
                toast.success(bl(
                    `+${res.credits_refunded} credits refunded`,
                    `+${res.credits_refunded} crédits remboursés`,
                    locale,
                ));
            } else if (newRating === "down" && res.capped) {
                toast.warning(bl("Monthly refund cap reached (20%)", "Plafond mensuel atteint (20%)", locale));
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    }

    function toggle(id) {
        const s = new Set(selected);
        s.has(id) ? s.delete(id) : s.add(id);
        setSelected(s);
    }
    function selectAllVisible(list) {
        setSelected(new Set(list.map(l => l.lead_id)));
    }
    function clearSelection() { setSelected(new Set()); }

    const noEmail = items.filter(l => !l.email);

    async function bulkReveal(field) {
        if (!selected.size) { toast.info("Select leads first"); return; }
        setBusy(field === "email" ? "reveal-email" : "reveal-phone");
        try {
            const res = await extrasApi.bulkReveal([...selected], field);
            toast.success(`${res.revealed} ${field}s revealed · ${res.spent} credits spent`);
            refresh(); clearSelection(); load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally { setBusy(null); }
    }

    async function aiGuessEmails() {
        if (!selected.size) { toast.info(bl("Select leads first", "Sélectionne des leads", locale)); return; }
        setBusy("ai");
        const toastId = toast.loading(bl(`AI guessing ${selected.size} emails…`, `IA devine ${selected.size} emails…`, locale));
        try {
            const res = await extrasApi.enrich.aiGuess([...selected]);
            toast.success(bl(`${res.found} emails guessed · ${res.credits_spent} credits spent`,
                             `${res.found} emails devinés · ${res.credits_spent} crédits`, locale), { id: toastId });
            refresh(); clearSelection(); load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed", { id: toastId });
        } finally { setBusy(null); }
    }

    async function hunterEmails() {
        if (!selected.size) { toast.info(bl("Select leads first", "Sélectionne des leads", locale)); return; }
        setBusy("hunter");
        const toastId = toast.loading(bl(`Hunter.io searching ${selected.size} emails…`, `Hunter.io cherche ${selected.size} emails…`, locale));
        try {
            const res = await extrasApi.enrich.hunter([...selected]);
            toast.success(bl(`${res.found} verified emails · ${res.credits_spent} credits spent`,
                             `${res.found} emails vérifiés · ${res.credits_spent} crédits`, locale), { id: toastId });
            refresh(); clearSelection(); load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed", { id: toastId });
        } finally { setBusy(null); }
    }

    async function xcrawlPhones() {
        if (!selected.size) { toast.info(bl("Select leads first", "Sélectionne des leads", locale)); return; }
        setBusy("xcrawl");
        const toastId = toast.loading(bl(`XCrawl searching ${selected.size} phones on Google…`, `XCrawl cherche ${selected.size} téléphones sur Google…`, locale));
        try {
            const res = await extrasApi.enrich.xcrawlPhone([...selected]);
            toast.success(bl(`${res.found} phones found · ${res.credits_spent} credits spent`,
                             `${res.found} téléphones trouvés · ${res.credits_spent} crédits`, locale), { id: toastId });
            refresh(); clearSelection(); load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed", { id: toastId });
        } finally { setBusy(null); }
    }

    return (
        <div className="px-6 lg:px-10 py-6">
            <div className="mb-6">
                <h1 className="font-display font-black text-3xl tracking-tighter">{bl("Enrichment center", "Centre d'enrichissement", locale)}</h1>
                <p className="mt-1 text-sm text-zinc-500 max-w-3xl">
                    {bl("Fill in missing emails and phones. Choose your strategy — AI pattern (cheap, ~50% accurate) or Hunter.io (verified, 95%+).",
                        "Complète les emails et téléphones manquants. Choisis ta stratégie — IA pattern (économe, ~50% correct) ou Hunter.io (vérifié, 95%+).", locale)}
                </p>
            </div>

            {/* Strategy cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="rounded-2xl p-5 bg-brand-surface border border-white/8" data-testid="enrich-strat-ai">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-violet to-brand-indigo flex items-center justify-center shrink-0">
                            <Wand2 size={16} className="text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <div className="font-display font-bold text-white">{bl("AI pattern (Plan B)", "IA pattern (Plan B)", locale)}</div>
                                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-brand-cyan/15 text-brand-cyan border border-brand-cyan/30">1 crédit / lead</span>
                            </div>
                            <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
                                {bl("GPT-5.2 guesses email patterns from name + company. 40-60% deliverability. Good for cheap volume, not for cold outreach at scale.",
                                    "GPT-5.2 devine l'email depuis nom + société. 40-60% de délivrabilité. Bon pour volume économe, pas idéal pour outreach massif.", locale)}
                            </p>
                            <button
                                type="button"
                                onClick={aiGuessEmails}
                                disabled={!selected.size || busy !== null}
                                data-testid="enrich-ai-guess"
                                className="mt-3 btn-cta text-white font-semibold text-sm px-4 py-2 rounded-full inline-flex items-center gap-2 disabled:opacity-40"
                            >
                                {busy === "ai" ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />}
                                {bl("Guess", "Deviner", locale)} ({selected.size}c)
                            </button>
                        </div>
                    </div>
                </div>

                <div className={`rounded-2xl p-5 bg-brand-surface border ${providers?.hunter?.enabled ? "border-emerald-500/30" : "border-white/8"}`} data-testid="enrich-strat-hunter">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-brand-cyan flex items-center justify-center shrink-0">
                            <ShieldCheck size={16} className="text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <div className="font-display font-bold text-white">{bl("Hunter.io (Plan A)", "Hunter.io (Plan A)", locale)}</div>
                                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">3 crédits / lead</span>
                                {providers && !providers.hunter?.enabled && (
                                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">{bl("key needed", "clé requise", locale)}</span>
                                )}
                            </div>
                            <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
                                {bl("Real Hunter API — verified emails with 95%+ deliverability, score, and domain resolution. Add HUNTER_API_KEY to unlock (free tier: 50/month).",
                                    "Vraie API Hunter — emails vérifiés 95%+ de délivrabilité, score, résolution de domaine. Ajoute HUNTER_API_KEY pour débloquer (tier gratuit : 50/mois).", locale)}
                            </p>
                            <button
                                type="button"
                                onClick={hunterEmails}
                                disabled={!selected.size || busy !== null || !providers?.hunter?.enabled}
                                data-testid="enrich-hunter"
                                className="mt-3 btn-cta text-white font-semibold text-sm px-4 py-2 rounded-full inline-flex items-center gap-2 disabled:opacity-40"
                            >
                                {busy === "hunter" ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />}
                                {bl("Reveal verified", "Révéler vérifié", locale)} ({selected.size * 3}c)
                            </button>
                        </div>
                    </div>
                </div>

                <div className={`rounded-2xl p-5 bg-brand-surface border ${providers?.xcrawl_phone?.enabled ? "border-brand-cyan/30" : "border-white/8"}`} data-testid="enrich-strat-xcrawl">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-cyan to-brand-indigo flex items-center justify-center shrink-0">
                            <PhoneCall size={16} className="text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <div className="font-display font-bold text-white">{bl("XCrawl phones (Plan C)", "XCrawl téléphones (Plan C)", locale)}</div>
                                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-brand-cyan/15 text-brand-cyan border border-brand-cyan/30">3 crédits / lead</span>
                                {providers && !providers.xcrawl_phone?.enabled && (
                                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">{bl("key needed", "clé requise", locale)}</span>
                                )}
                            </div>
                            <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
                                {bl("Google SERP scan via XCrawl (legal proxy). Finds phone numbers where INSEE has none — 60-80% hit rate on French SMEs.",
                                    "Scan SERP Google via XCrawl (proxy légal). Trouve les téléphones que l'INSEE n'a pas — 60-80% de succès sur PME françaises.", locale)}
                            </p>
                            <button
                                type="button"
                                onClick={xcrawlPhones}
                                disabled={!selected.size || busy !== null || !providers?.xcrawl_phone?.enabled}
                                data-testid="enrich-xcrawl-phone"
                                className="mt-3 btn-cta text-white font-semibold text-sm px-4 py-2 rounded-full inline-flex items-center gap-2 disabled:opacity-40"
                            >
                                {busy === "xcrawl" ? <Loader2 size={13} className="animate-spin" /> : <PhoneCall size={13} />}
                                {bl("Find phones", "Trouver téléphones", locale)} ({selected.size * 3}c)
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Actions row */}
            <div className="mb-4 flex flex-wrap items-center gap-3">
                <button
                    type="button"
                    onClick={() => selectAllVisible(noEmail)}
                    data-testid="enrich-select-hidden-email"
                    className="text-sm text-white px-3 py-1.5 rounded-full border border-white/12 hover:border-brand-cyan/50 hover:bg-brand-cyan/5 inline-flex items-center gap-2"
                >
                    <Mail size={13} /> {bl("Select missing emails", "Sélect. emails manquants", locale)} ({noEmail.length})
                </button>
                <button
                    type="button"
                    onClick={clearSelection}
                    className="text-xs text-zinc-400 hover:text-white"
                >
                    {bl("Clear", "Effacer", locale)} ({selected.size})
                </button>
                <div className="flex-1" />
                <button type="button" onClick={() => bulkReveal("email")} disabled={busy !== null || !selected.size} data-testid="enrich-bulk-email" className="text-white text-sm font-medium px-4 py-2 rounded-full border border-white/12 hover:border-brand-cyan/50 hover:bg-brand-cyan/5 disabled:opacity-40 inline-flex items-center gap-2">
                    {busy === "reveal-email" ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} {bl("Reveal fake emails", "Révéler emails fake", locale)} ({selected.size}c)
                </button>
                <button type="button" onClick={() => bulkReveal("phone")} disabled={busy !== null || !selected.size} data-testid="enrich-bulk-phone" className="text-white text-sm font-medium px-4 py-2 rounded-full border border-white/12 hover:border-brand-cyan/50 hover:bg-brand-cyan/5 disabled:opacity-40 inline-flex items-center gap-2">
                    <Phone size={13} /> {bl("Reveal phones", "Révéler tél.", locale)} ({selected.size * 2}c)
                </button>
            </div>

            <div className="rounded-2xl border border-white/8 bg-brand-surface overflow-hidden">
                {loading ? (
                    <div className="py-24 text-center text-zinc-500"><Loader2 className="animate-spin inline mr-2" /> Loading…</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-xs uppercase tracking-wide text-zinc-500 border-b border-white/8 bg-white/[0.02]"><tr>
                                <th className="p-3 w-10"></th>
                                <th className="p-3 text-left">Lead</th>
                                <th className="p-3 text-left">Email</th>
                                <th className="p-3 text-left">Phone</th>
                                <th className="p-3 text-left">Score</th>
                                <th className="p-3 text-center">{bl("Rate", "Note", locale)}</th>
                            </tr></thead>
                            <tbody>
                                {items.map(l => {
                                    const r = ratings[l.lead_id];
                                    return (
                                    <tr key={l.lead_id} className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={`enrich-row-${l.lead_id}`}>
                                        <td className="p-3"><Checkbox checked={selected.has(l.lead_id)} onCheckedChange={() => toggle(l.lead_id)} /></td>
                                        <td className="p-3">
                                            <div className="flex items-center gap-2">
                                                <Avatar seed={l.avatar_seed} size={26} />
                                                <div>
                                                    <div className="text-white">{l.full_name}</div>
                                                    <div className="text-xs text-zinc-500">{l.title} @ {l.company_name}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-3 font-mono text-xs">
                                            {l.email ? (
                                                <div className="flex items-center gap-2">
                                                    <span className={l.email_status === "verified" ? "text-emerald-300" : "text-amber-300"}>{l.email}</span>
                                                    {typeof l.email_confidence === "number" && (
                                                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${l.email_status === "verified" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>{l.email_confidence}%</span>
                                                    )}
                                                </div>
                                            ) : <span className="text-zinc-500">{l.email_hidden}</span>}
                                        </td>
                                        <td className="p-3 font-mono text-xs">{l.phone ? <span className="text-emerald-300">{l.phone}</span> : <span className="text-zinc-500">{l.phone_hidden}</span>}</td>
                                        <td className="p-3 font-mono text-brand-cyan">{l.score}</td>
                                        <td className="p-3">
                                            <div className="flex items-center justify-center gap-1">
                                                <button
                                                    type="button"
                                                    onClick={() => rateLead(l.lead_id, "up")}
                                                    data-testid={`enrich-rate-up-${l.lead_id}`}
                                                    className={`w-7 h-7 rounded-md inline-flex items-center justify-center transition ${r === "up" ? "bg-emerald-500/20 text-emerald-300" : "text-zinc-500 hover:text-emerald-300 hover:bg-white/5"}`}
                                                    title={bl("Good lead", "Bon lead", locale)}
                                                >
                                                    <ThumbsUp size={12} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => rateLead(l.lead_id, "down")}
                                                    data-testid={`enrich-rate-down-${l.lead_id}`}
                                                    className={`w-7 h-7 rounded-md inline-flex items-center justify-center transition ${r === "down" ? "bg-rose-500/20 text-rose-300" : "text-zinc-500 hover:text-rose-300 hover:bg-white/5"}`}
                                                    title={bl("Bad lead → refund", "Mauvais → remboursement", locale)}
                                                >
                                                    <ThumbsDown size={12} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );})}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            <div className="mt-3 text-xs text-zinc-500 font-mono flex items-center gap-2 flex-wrap">
                <Info size={11} /> {total} leads · showing first 100 · {bl("guessed emails shown in amber, verified in green", "emails devinés en ambre, vérifiés en vert", locale)}
                {quota && (
                    <span className="ml-auto text-brand-cyan/80">
                        {bl(
                            `Refund quota this month: ${quota.month_refunded}/${quota.monthly_cap}c (20% of spend)`,
                            `Quota remboursement du mois: ${quota.month_refunded}/${quota.monthly_cap}c (20% de la dépense)`,
                            locale,
                        )}
                    </span>
                )}
            </div>
        </div>
    );
}
